import { queryClient } from "@shared/api/queries/queryClient";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { Tabs } from "@shared/ui/tabs";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { ChevronLeft, Info } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBillCycleConsumerDashboard,
  useBillCycleDetailsForEdit,
  useCategoryConfigMap,
  useConsumerList,
  useSubCategoryConfigMap,
  useSubmitBillCycle,
  useUpdateBillCycle,
} from "../../hooks";
import { BillCycleConsumerList } from "../BillCycleConsumerList";
import { PremisesTab } from "../premiseTable";
import { logEvent } from "@shared/analytics/analytics";

// Categories and subcategories data
const categoriesData = [
  {
    value: "commercial",
    label: "Commercial",
    subcategories: ["Retail", "Office", "Food", "Entertainment"],
  },
  {
    value: "residential",
    label: "Residential",
    subcategories: ["Apartment", "House", "Condominium"],
  },
  {
    value: "industrial",
    label: "Industrial",
    subcategories: ["Manufacturing", "Warehouse", "Processing"],
  },
  {
    value: "public",
    label: "Public",
    subcategories: ["Government", "Parks", "Schools"],
  },
];

const CreateBillCycle = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
   const remoteUtilityId = getRemoteUtilityId();
  const { data: categoryConfigMap = [], isPending: isCategoryLoading } =
    useCategoryConfigMap();
  const [subCategoryOptions, setSubCategoryOptions] = useState<any[]>([]);
  const formFields: FormField[] = [
    {
      name: "bill_cycle_name",
      label: "Bill Cycle Name",
      type: "text",
      required: true,
      placeholder: "Enter a unique name for this cycle",
      className: "col-span-2",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 h-10 px-3",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "category",
      label: "Consumer Categories",
      type: "multi-select",
      required: true,
      options: categoryConfigMap.map((category: any) => ({
        value: category.id,
        label: category.name,
      })),
      placeholder: "Select consumer categories",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "sub_category",
      label: "Subcategories",
      type: "multi-select",
      required: true,
      options: subCategoryOptions,
      placeholder: "Select consumer subcategories",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "duration",
      label: "Billing Duration (Days)",
      type: "number",
      required: true,
      className: "col-span-2",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 h-10 px-3",
        error: "text-red-600 text-sm mt-1",
      },
      customValidation: (value: any) => {
        const numValue = Number(value);

        if (isNaN(numValue)) {
          return "Please enter a valid number";
        }

        if (numValue < 0) {
          return "Duration cannot be negative";
        }

        if (numValue === 0) {
          return "Duration must be at least 1 day";
        }

        if (numValue % 1 !== 0) {
          return "Please enter a whole number of days";
        }

        return true; // Valid
      },
    },
  ];
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const watchedValues = form.watch();

  const { data: subCategoryList = [], isPending: isSubCategoryLoading } =
    useSubCategoryConfigMap(
      watchedValues.category && // Changed from consumerCategory to category
        Array.isArray(watchedValues.category)
        ? watchedValues.category
        : []
    );
  useEffect(() => {
    setSubCategoryOptions(
      subCategoryList.map((subCategory: any) => ({
        value: subCategory.id,
        label: subCategory.name,
      }))
    );
  }, [subCategoryList]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(
    []
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);
  const [selectedConsumers, setSelectedConsumers] = useState<string[]>([]);
  const [currentPremiseList, setCurrentPremiseList] = useState<any[]>([]);

  const params = useParams();

  const { data: billCycleDetails } = useBillCycleDetailsForEdit(
    params.action === "edit" ? params.id : undefined
  );

  const [consumerQueryParams, setConsumerQueryParams] = useState({
    page: 1,
    status: "",
    search_data: "",
    area: [],
    sub_area: [],
  });

  useEffect(() => {
    if (params.action === "edit" && billCycleDetails?.billCycleName) {
      form.setValue("bill_cycle_name", billCycleDetails?.billCycleName);
      form.setValue("category", billCycleDetails?.category);
      form.setValue("sub_category", billCycleDetails?.subCategory);
      setSelectedPremises(billCycleDetails?.premise);
    }
  }, [params.action, billCycleDetails, form]);
  const updateBillCycle = useUpdateBillCycle(params.id);
  const submitBillCycle = useSubmitBillCycle();

  const { mutate: consumerDashboard,data: consumerDashboardData } = useBillCycleConsumerDashboard();
  const { mutate: consumerList,data: consumerListData } = useConsumerList();

  const handlePremiseSelectionChange = useCallback((selectedIds: any) => {
    if(selectedIds.length){
      consumerDashboard({
        remote_utility_id: remoteUtilityId,
        is_category_count: 1,
        premise: selectedIds,
      });
      consumerList({
        remote_utility_id: remoteUtilityId,
        premise: selectedIds,
        is_pagination: 1,
        is_extra_details: 1,
      });
    }
    setSelectedPremises(selectedIds);
  }, []);


  useEffect(() => {
  if (selectedPremises?.length) {
    consumerDashboard({
      remote_utility_id: remoteUtilityId,
      is_category_count: 1,
      premise: selectedPremises,
    });

    consumerList({
      remote_utility_id: remoteUtilityId,
      premise: selectedPremises,
      is_pagination: 1,
      is_extra_details: 1,
    });
  }
}, [selectedPremises, remoteUtilityId]);



  const handleConsumersSelectionChange = useCallback((selectedIds: any) => {
    setSelectedConsumers(selectedIds);
  }, []);


  const handleConsumerFiltersChange = useCallback(
    (newParams) => {
      setConsumerQueryParams((prev) => ({
        ...prev,
        ...newParams,
      }));
    },
    [consumerQueryParams]
  );

  const tabsConfig = {
    premises: {
      label: "Premises",
      component: (
        <PremisesTab
          selectedPremises={selectedPremises}
          onSelectionChange={handlePremiseSelectionChange}
          onPremiseListChange={setCurrentPremiseList}
        />
      ),
    },
    consumers: {
      label: "Consumers",
      component: (
        <BillCycleConsumerList
          onSetFilters={handleConsumerFiltersChange} // Add this prop
          consumersList={consumerListData?.result}
          selectedConsumers={selectedConsumers}
          onSelectionChange={handleConsumersSelectionChange}
          queryParams={consumerQueryParams} // Pass current params
        />
      ),
    },
  };

  // Handle subcategory selection
  const handleSubcategoryChange = (subcategory: string) => {
    setSelectedSubcategory(subcategory);
    if (!selectedSubcategories.includes(subcategory)) {
      setSelectedSubcategories([...selectedSubcategories, subcategory]);
    }
  };

  const handleSave = () => {
    const selectedItemsDetails = selectedPremises.map((code) => {
      const item = currentPremiseList.find((entry) => entry.code === code);
      return {
        premise: code,
        area: item?.hierarchyCodes?.area || null,
        sub_area: item?.hierarchyCodes?.subArea || null,
      };
    });
    const formValues = form.getValues();

    const areaList = selectedItemsDetails.map((item) => item.area);
    const subAreaList = selectedItemsDetails.map((item) => item.sub_area);
    const premises = selectedItemsDetails.map((item) => item.premise);

    const payload = {
      bill_cycle_name: formValues.bill_cycle_name,
      utility_services: [],
      category: formValues.category,
      sub_category: formValues.sub_category,
      cycle_duration: formValues.duration,
      premise: premises,
      // area: areaList,
      // sub_area: subAreaList,
      consumer_count: consumerDashboardData?.result?.total,
      remote_utility_id: getRemoteUtilityId(),
    };

    if (params.action == "edit") {
      updateBillCycle.mutate(payload, {
        onSuccess: async (data) => {
          logEvent("Billing cycle Modified", {
            ...payload,
            timestamp: Date.now(),
          });
          await queryClient.refetchQueries({
            queryKey: [["bx", "billing", "list", "billing-cycle-list"]],
          });
          await queryClient.refetchQueries({
            queryKey: [["bx", "billing", "details", "bill-cycle-details-Edit"]],
            exact: false,
          });
        },
      });
    } else {
      submitBillCycle.mutate(payload, {
        onSuccess: async (data) => {
          logEvent("Billing cycle Created", {
            ...payload,
            timestamp: Date.now(),
          });
          await queryClient.refetchQueries({
            queryKey: [["bx", "billing", "list", "billing-cycle-list"]],
          });
        },
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header with back button */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex flex-col">
              <h1 className="page-title">
                {params.action === "edit"
                  ? "Edit Bill Cycle"
                  : "Create New Bill Cycle"}
              </h1>
              <p className="page-subtitle">
                Set up and define a new billing period for consumers.
              </p>
            </span>
          </div>
        </div>
      </div>

      {/* Main content with split layout */}
      <div className=" max-w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Panel (3/5 width) */}
          <div className="lg:col-span-3 space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="page-subheading">
                  Bill Cycle Configuration
                </CardTitle>
                <CardDescription className="page-subtitle">
                  Configure bill cycle parameters to manage billing for selected
                  consumers
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-7">
                <DynamicForm
                  fields={formFields}
                  form={form}
                  onSubmit={handleSave}
                  gridColumns={2}
                  responsiveColumns={{ sm: 1, md: 2 }}
                  config={{
                    layout: "vertical",
                    spacing: "small",
                    showSubmitButton: false,
                    showResetButton: false,
                    classes: {
                      form: "space-y-4",
                      fieldContainer: "w-full",
                    },
                  }}
                />
              </CardContent>
            </Card>

            {/* Bottom Half - Tabbed Premises/Consumers */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Premise Selection</CardTitle>
                <CardDescription>
                  Select premises to include in this bill cycle
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {/* Information message about billing consideration */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Info className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        Only Active and Temporary Disconnected consumers will be
                        considered for billing.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabs for Premises and Consumers */}
                <Tabs
                  defaultValue="premises"
                  tabComponents={tabsConfig}
                  idPrefix="bill-cycle"
                  tabsListClassName="grid grid-cols-2"
                />

                {/* Action buttons moved below the table */}
                <div className="flex gap-3 justify-end mt-6">
                  <Button variant="outline" onClick={() => navigate("/")}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>Save</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel (2/5 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Top Section - Total Consumers */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Total Consumers</CardTitle>
                <CardDescription>
                  Overall consumer count for selected premises
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-blue-600 mt-4">
                    {consumerDashboardData?.result?.total ?? "NA"}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Middle Section - Category Breakdown */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Category Breakdown</CardTitle>
                <CardDescription>
                  Distribution of consumers by category
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 pt-4">
                  {consumerDashboardData?.result?.categoryData?.length > 0 ? (
                    consumerDashboardData.result.categoryData.map((category) => (
                      <div key={category.category} className="space-y-1">
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span>{category.category ?? "NA"}</span>
                          <span className="font-medium">
                            {category.consumerCount ?? "NA"} (
                            {category.percentage ?? "NA"}%)
                          </span>
                        </div>
                        <Progress value={category.percentage} className="h-2" />
                      </div>
                    ))
                  ) : (
                    <ul className="text-sm text-gray-500 list-disc pl-5">
                      <li>No category data available</li>
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Connection Status</CardTitle>
                <CardDescription>
                  Current status of consumer connections
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Visual representation of status distribution */}
                <div className="flex justify-around my-4">
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full border-8 border-green-500 flex items-center justify-center mb-2">
                      <span className="card-value-text">
                        {consumerDashboardData?.result?.activePercent ?? "NA"}%
                      </span>
                    </div>
                    <span className="text-xs">Active</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full border-8 border-amber-500 flex items-center justify-center mb-2">
                      <span className="card-value-text">
                        {consumerDashboardData?.result?.pausedPercent ?? "NA"}%
                      </span>
                    </div>
                    <span className="text-xs">Paused</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full border-8 border-gray-400 flex items-center justify-center mb-2">
                      <span className="card-value-text">
                        {consumerDashboardData?.result?.disconnectedPercent ?? "NA"}%
                      </span>
                    </div>
                    <span className="text-xs">Disconnected</span>
                  </div>
                </div>

                {/* Detailed status count */}
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2 text-xs"></span>
                      Active
                    </span>
                    <span>{consumerDashboardData?.result?.active ?? "NA"} consumers</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-amber-500 rounded-full mr-2"></span>
                      Paused
                    </span>
                    <span>{consumerDashboardData?.result?.paused ?? "Na"} consumers</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
                      Temporary Distribution of consumers by category
                    </span>
                    <span>
                      {consumerDashboardData?.result?.disconnectedPercent ?? "NA"} consumers
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBillCycle;
